/**
 * Fenêtres du menu : nouvelle partie, rejoindre avec un code, réglages et suppression
 * d'une partie (gérant), invitation d'amis, profil (protéger un compte invité).
 */
import { useMemo, useState } from 'preact/hooks';
import { KeyRound, Mountain, Play, Plus, Settings, ShieldCheck, Shuffle, Trash2, UserPlus, Users } from 'lucide-preact';
import { DEFAULT_MAX_PLAYERS, MAX_PLAYERS_CHOICES, VAL_CENDREE, WORLD_NAME_RE, WORLD_VISIBILITIES, mapById, type ServerSummary, type WorldVisibility } from '@snowchain/shared';
import { claim } from '../auth';
import { toast } from '../hud/ui-state';
import { lobby } from './lobby';
import { MapPicker } from './maps';
import { menu, menuActions, type MenuDialog } from './state';
import { act, inviteFriend, join, joinState } from './actions';
import { Avatar, Btn, Dialog, Field, LiveTag, Segmented, VISIBILITY, VisibilityTag, gameDate, parseCode, presenceText } from './ui';

const STATION_NAMES = [
  'Chalet du Sommet',
  'Les Cimes Blanches',
  'Val d’Argent',
  'Le Grand Cerf',
  'Les Hauts de Cendrée',
  'Combe aux Mélèzes',
  'Pic de l’Aube',
  'Les Balcons du Lac',
  'Plan des Marmottes',
  'Col des Givres',
];

const close = () => (menu.dialog.value = null);

function cleanName(s: string): string {
  return s.normalize('NFKC').replace(/\s+/g, ' ').trim();
}

function nameError(s: string): string | null {
  return WORLD_NAME_RE.test(cleanName(s)) ? null : '3 à 32 caractères : lettres, chiffres, espaces et ponctuation simple';
}

function VisibilityPicker(props: { value: WorldVisibility; onChange: (v: WorldVisibility) => void }) {
  return (
    <div class="mn-vis" role="radiogroup" aria-label="Visibilité">
      {WORLD_VISIBILITIES.map((v) => {
        const V = VISIBILITY[v];
        return (
          <button key={v} type="button" role="radio" aria-checked={props.value === v} class={`mn-vis-opt${props.value === v ? ' on' : ''}`} onClick={() => props.onChange(v)}>
            <V.icon size={18} />
            <span class="mn-vis-label">{V.label}</span>
            <span class="mn-vis-hint">{V.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function PlayersPicker(props: { value: number; onChange: (n: number) => void }) {
  return <Segmented label="Joueurs maximum" value={props.value} onChange={props.onChange} options={MAX_PLAYERS_CHOICES.map((n) => ({ value: n, label: String(n) }))} />;
}

function CreateDialog() {
  const me = lobby.me.value;
  const defaultWorld = me && `Vallée de ${me.name}`.length <= 32 ? `Vallée de ${me.name}` : 'Val Cendrée';
  const [name, setName] = useState(defaultWorld);
  const [station, setStation] = useState(() => STATION_NAMES[Math.floor(Math.random() * STATION_NAMES.length)] as string);
  const [visibility, setVisibility] = useState<WorldVisibility>('public');
  const [maxPlayers, setMax] = useState(DEFAULT_MAX_PLAYERS);
  const [mapId, setMapId] = useState(VAL_CENDREE.id);
  const [busy, setBusy] = useState(false);
  const errName = nameError(name);
  const errStation = nameError(station);
  const submit = async () => {
    if (errName || errStation || busy) return;
    setBusy(true);
    const r = await act({ op: 'world.create', name: cleanName(name), stationName: cleanName(station), visibility, maxPlayers, mapId });
    setBusy(false);
    if (!r) return;
    lobby.upsertServer(r.server);
    close();
    join(r.server);
  };
  return (
    <Dialog
      title="Nouvelle partie"
      icon={Plus}
      wide
      onClose={close}
      footer={
        <>
          <span class="mn-muted mn-small">
            Carte : {mapById(mapId).name} · 320 000 € de départ
          </span>
          <Btn kind="ghost" onClick={close}>
            Annuler
          </Btn>
          <Btn kind="primary" icon={Play} disabled={!!errName || !!errStation || busy} onClick={() => void submit()}>
            {busy ? 'Création...' : 'Créer et jouer'}
          </Btn>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div class="mn-form-2">
          <Field label="Nom de la partie" for="mn-c-name" hint={errName ?? 'Affiché dans la liste des serveurs.'}>
            <input id="mn-c-name" value={name} maxLength={32} onInput={(e) => setName((e.target as HTMLInputElement).value)} class={errName ? 'bad' : ''} />
          </Field>
          <Field label="Nom de votre station" for="mn-c-station" hint={errStation ?? 'Le nom que verront vos skieurs.'}>
            <div class="mn-input-btn">
              <input id="mn-c-station" value={station} maxLength={32} onInput={(e) => setStation((e.target as HTMLInputElement).value)} class={errStation ? 'bad' : ''} />
              <Btn
                kind="ghost"
                icon={Shuffle}
                title="Autre nom"
                onClick={() => {
                  const others = STATION_NAMES.filter((s) => s !== station);
                  setStation(others[Math.floor(Math.random() * others.length)] as string);
                }}
              />
            </div>
          </Field>
        </div>
        <Field label="Carte" hint="Les grandes cartes ont plusieurs versants : la station démarre au village, le reste du massif est à équiper.">
          <MapPicker value={mapId} onChange={setMapId} />
        </Field>
        <Field label="Visibilité">
          <VisibilityPicker value={visibility} onChange={setVisibility} />
        </Field>
        <Field label="Joueurs connectés en même temps" hint="Vous gérez la station ; les autres joueurs l’observent en direct. Le gérant peut toujours entrer.">
          <PlayersPicker value={maxPlayers} onChange={setMax} />
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function CodeDialog(props: { code?: string }) {
  const [raw, setRaw] = useState(props.code ?? '');
  const [found, setFound] = useState<ServerSummary | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const look = async () => {
    const code = parseCode(raw);
    if (!code) {
      setError('Collez un code (par exemple xYz12AbC) ou un lien d’invitation');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await lobby.req({ op: 'world.get', id: code });
      setFound(r.server);
    } catch (e) {
      setFound(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const st = found ? joinState(found) : null;
  return (
    <Dialog
      title="Rejoindre avec un code"
      icon={KeyRound}
      onClose={close}
      footer={
        <>
          <Btn kind="ghost" onClick={close}>
            Annuler
          </Btn>
          {found ? (
            <Btn
              kind="primary"
              icon={Play}
              disabled={!!st?.blocked}
              title={st?.blocked ?? undefined}
              onClick={() => {
                close();
                join(found);
              }}
            >
              {st?.label ?? 'Rejoindre'}
            </Btn>
          ) : (
            <Btn kind="primary" disabled={busy || !raw.trim()} onClick={() => void look()}>
              {busy ? 'Recherche...' : 'Rechercher'}
            </Btn>
          )}
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void look();
        }}
      >
        <Field label="Code ou lien de la partie" for="mn-code" hint="Le gérant le trouve dans la fiche de sa partie (« Lien » ou « Code »).">
          <input
            id="mn-code"
            value={raw}
            spellcheck={false}
            autoComplete="off"
            placeholder="xYz12AbC ou https://.../?world=xYz12AbC"
            onInput={(e) => {
              setRaw((e.target as HTMLInputElement).value);
              setFound(null);
              setError('');
            }}
          />
        </Field>
      </form>
      {error && <div class="mn-err">{error}</div>}
      {found && (
        <div class="mn-found">
          <div class="mn-found-title">
            <Mountain size={16} />
            <b>{found.stationName || found.name}</b>
          </div>
          <div class="mn-muted">
            {found.name} · hôte {found.owner?.name ?? 'aucun'} · Jour {gameDate(found.gameTime).day}
          </div>
          <div class="mn-found-tags">
            <VisibilityTag v={found.visibility} />
            <LiveTag live={found.live} />
            <span class="mn-pill">
              <Users size={11} /> {found.online.length}/{found.maxPlayers}
            </span>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function SettingsDialog(props: { server: ServerSummary }) {
  const s = props.server;
  const [name, setName] = useState(s.name);
  const [visibility, setVisibility] = useState<WorldVisibility>(s.visibility);
  const [maxPlayers, setMax] = useState(s.maxPlayers);
  const [busy, setBusy] = useState(false);
  const err = nameError(name);
  const changed = cleanName(name) !== s.name || visibility !== s.visibility || maxPlayers !== s.maxPlayers;
  const save = async () => {
    if (err || busy || !changed) return;
    setBusy(true);
    const r = await act(
      { op: 'world.update', id: s.id, ...(cleanName(name) !== s.name ? { name: cleanName(name) } : {}), ...(visibility !== s.visibility ? { visibility } : {}), ...(maxPlayers !== s.maxPlayers ? { maxPlayers } : {}) },
      'Partie mise à jour',
    );
    setBusy(false);
    if (!r) return;
    lobby.upsertServer(r.server);
    close();
  };
  return (
    <Dialog
      title="Paramètres de la partie"
      icon={Settings}
      wide
      onClose={close}
      footer={
        <>
          <Btn kind="ghost" onClick={close}>
            Annuler
          </Btn>
          <Btn kind="primary" disabled={!!err || busy || !changed} onClick={() => void save()}>
            {busy ? 'Enregistrement...' : 'Enregistrer'}
          </Btn>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Nom de la partie" for="mn-s-name" hint={err ?? 'Les joueurs connectés voient le nouveau nom aussitôt.'}>
          <input id="mn-s-name" value={name} maxLength={32} onInput={(e) => setName((e.target as HTMLInputElement).value)} class={err ? 'bad' : ''} />
        </Field>
        <Field label="Visibilité" hint="Le code de la partie reste valable quelle que soit la visibilité.">
          <VisibilityPicker value={visibility} onChange={setVisibility} />
        </Field>
        <Field label="Joueurs connectés en même temps" hint={s.online.length > maxPlayers ? `${s.online.length} joueurs sont connectés : personne n’est exclu, mais plus personne n’entrera.` : undefined}>
          <PlayersPicker value={maxPlayers} onChange={setMax} />
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function DeleteDialog(props: { server: ServerSummary }) {
  const s = props.server;
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const others = s.online.filter((p) => p.id !== lobby.me.value?.id).length;
  return (
    <Dialog
      title="Supprimer la partie"
      icon={Trash2}
      tone="danger"
      onClose={close}
      footer={
        <>
          <Btn kind="ghost" onClick={close}>
            Annuler
          </Btn>
          <Btn
            kind="danger"
            icon={Trash2}
            disabled={!sure || busy}
            onClick={async () => {
              setBusy(true);
              const r = await act({ op: 'world.delete', id: s.id }, 'Partie supprimée', `« ${s.name} » a été effacée.`);
              setBusy(false);
              if (!r) return;
              lobby.removeServer(s.id);
              if (menu.selected.value === s.id) menu.selected.value = null;
              close();
            }}
          >
            Supprimer définitivement
          </Btn>
        </>
      }
    >
      <p>
        « <b>{s.name}</b> » et la station <b>{s.stationName}</b> seront effacées : bâtiments, remontées, finances, historique et invitations en cours.{' '}
        {s.members > 1 ? `${s.members - 1} autre${s.members > 2 ? 's' : ''} joueur${s.members > 2 ? 's' : ''} la perdront aussi de leurs parties.` : ''}
      </p>
      {others > 0 && <div class="mn-err">Des joueurs sont connectés : la suppression sera refusée tant que la partie n’est pas vide.</div>}
      <label class="mn-check mn-check-block">
        <input type="checkbox" checked={sure} onChange={(e) => setSure((e.target as HTMLInputElement).checked)} />
        Je comprends que cette suppression est définitive.
      </label>
    </Dialog>
  );
}

function InviteDialog(props: { server: ServerSummary }) {
  const s = props.server;
  const friends = lobby.social.value.friends;
  const [sent, setSent] = useState<Set<string>>(new Set());
  const here = new Set(s.online.map((p) => p.id));
  return (
    <Dialog title={`Inviter sur « ${s.name} »`} icon={UserPlus} onClose={close} footer={<Btn onClick={close}>Fermer</Btn>}>
      {friends.length === 0 ? (
        <p class="mn-muted">Ajoutez d’abord des amis depuis la page Amis.</p>
      ) : (
        <div class="mn-list mn-list-tight">
          {friends.map((f) => (
            <div class="mn-friend" key={f.id}>
              <Avatar id={f.id} name={f.name} size={30} presence={f.presence.state} />
              <div class="mn-friend-text">
                <span class="mn-friend-name">{f.name}</span>
                <span class="mn-friend-presence">{here.has(f.id) ? 'Déjà dans la partie' : presenceText(f.presence)}</span>
              </div>
              <span class="mn-friend-actions">
                <Btn
                  size="sm"
                  kind={sent.has(f.id) ? 'ghost' : 'secondary'}
                  disabled={sent.has(f.id) || here.has(f.id)}
                  onClick={async () => {
                    if (await inviteFriend(f, s)) setSent(new Set([...sent, f.id]));
                  }}
                >
                  {sent.has(f.id) ? 'Invité' : 'Inviter'}
                </Btn>
              </span>
            </div>
          ))}
        </div>
      )}
      <p class="mn-small mn-muted">Une invitation reste valable 12 heures. Elle donne accès à la partie même si elle est privée.</p>
    </Dialog>
  );
}

function ProfileDialog() {
  const me = lobby.me.value;
  const [name, setName] = useState(me?.name ?? '');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const guest = me?.kind === 'guest';
  const mismatch = pw2.length > 0 && pw !== pw2;
  const submit = async () => {
    if (!me || busy || pw.length < 8 || pw !== pw2) return;
    setBusy(true);
    setError('');
    try {
      const account = await claim(menuActions.token(), name.trim(), pw);
      lobby.me.value = { id: account.id, name: account.name, kind: account.kind };
      toast('ok', 'Compte protégé', 'Connectez-vous avec ce nom et ce mot de passe depuis n’importe quel appareil.');
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const footer = useMemo(
    () => (
      <>
        <Btn
          kind="ghost"
          onClick={() => {
            close();
            menuActions.logout();
          }}
        >
          Se déconnecter
        </Btn>
        {guest && (
          <Btn kind="primary" icon={ShieldCheck} disabled={busy || pw.length < 8 || pw !== pw2} onClick={() => void submit()}>
            {busy ? 'Protection...' : 'Protéger mon compte'}
          </Btn>
        )}
      </>
    ),
    [guest, busy, pw, pw2, name],
  );
  if (!me) return null;
  return (
    <Dialog title="Profil" icon={ShieldCheck} onClose={close} footer={footer}>
      <div class="mn-profile">
        <Avatar id={me.id} name={me.name} size={52} />
        <div>
          <div class="mn-profile-name">{me.name}</div>
          <div class="mn-muted">{guest ? 'Compte invité : lié à ce navigateur' : 'Compte protégé par mot de passe'}</div>
        </div>
      </div>
      {guest ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <p class="mn-small mn-muted">Ajoutez un mot de passe pour retrouver vos parties et vos amis sur un autre appareil. Vous gardez tout : même compte, mêmes stations.</p>
          <Field label="Nom de joueur" for="mn-p-name">
            <input id="mn-p-name" value={name} maxLength={24} autoComplete="username" onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </Field>
          <div class="mn-form-2">
            <Field label="Mot de passe" for="mn-p-pw" hint="8 caractères au moins">
              <input id="mn-p-pw" type="password" value={pw} autoComplete="new-password" onInput={(e) => setPw((e.target as HTMLInputElement).value)} />
            </Field>
            <Field label="Confirmation" for="mn-p-pw2" hint={mismatch ? 'Les deux mots de passe diffèrent' : undefined}>
              <input id="mn-p-pw2" type="password" value={pw2} autoComplete="new-password" class={mismatch ? 'bad' : ''} onInput={(e) => setPw2((e.target as HTMLInputElement).value)} />
            </Field>
          </div>
          <button type="submit" hidden />
        </form>
      ) : (
        <p class="mn-small mn-muted">Vos parties et vos amis vous suivent sur tous vos appareils.</p>
      )}
      {error && <div class="mn-err">{error}</div>}
    </Dialog>
  );
}

export function MenuDialogs() {
  const d: MenuDialog | null = menu.dialog.value;
  if (!d) return null;
  switch (d.k) {
    case 'create':
      return <CreateDialog />;
    case 'code':
      return <CodeDialog code={d.code} />;
    case 'settings':
      return <SettingsDialog server={d.server} />;
    case 'delete':
      return <DeleteDialog server={d.server} />;
    case 'invite':
      return <InviteDialog server={d.server} />;
    case 'profile':
      return <ProfileDialog />;
    case 'message':
      return (
        <Dialog title={d.title} onClose={close} footer={<Btn kind="primary" onClick={close}>Compris</Btn>}>
          <p>{d.message}</p>
        </Dialog>
      );
  }
}
